import React from "react";
import { EntityEditProvider } from "@/contexts/EntityEditContext";
import type { EntityEditProviderProps } from "@/types/contexts/entityEdit";
import { UserType } from "@/types/db/users";
import { userService } from "@/services/userService";
import { supabase } from "@/lib/supabase-client";

interface UserEditProviderProps extends Omit<EntityEditProviderProps<UserType>, "onSave" | "entityName"> {
  children: React.ReactNode;
}

export function UserEditProvider({ children, entityData, loading, onSaveSuccess }: UserEditProviderProps) {
  const handleSave = async (formData: Partial<UserType>, metadata?: Record<string, any>): Promise<UserType> => {
    if (!entityData?.id) {
      throw new Error("No user ID available");
    }

    // Check if email changed and validate it doesn't already exist
    const newEmail = formData.email_address?.trim().toLowerCase();
    const originalEmail = entityData.email_address?.trim().toLowerCase();
    if (newEmail && newEmail !== originalEmail) {
      const { data: existingUsers } = await supabase
        .from("users")
        .select("id")
        .ilike("email_address", newEmail)
        .neq("id", entityData.id)
        .limit(1);

      if (existingUsers && existingUsers.length > 0) {
        throw new Error("A user with this email address already exists");
      }
    }

    // Check if cell phone changed and validate it doesn't already exist
    const newCellPhone = formData.meta_data?.phones?.find((p: any) => p.type === "cell")?.number;
    const originalCellPhone = entityData.meta_data?.phones?.find((p: any) => p.type === "cell")?.number;
    const normalizedNewCell = newCellPhone?.replace(/\D/g, "");
    const normalizedOriginalCell = originalCellPhone?.replace(/\D/g, "");

    if (normalizedNewCell && normalizedNewCell.length >= 10 && normalizedNewCell !== normalizedOriginalCell) {
      const { data: existingUsers } = await supabase.from("users").select("id, meta_data").neq("id", entityData.id);

      if (existingUsers) {
        const cellExists = existingUsers.some((u) => {
          const phones = u.meta_data?.phones || [];
          return phones.some((p: any) => p.type === "cell" && p.number?.replace(/\D/g, "") === normalizedNewCell);
        });

        if (cellExists) {
          throw new Error("A user with this cell phone number already exists");
        }
      }
    }

    // Extract relational data that needs to be saved to separate tables
    const {
      id,
      created_at,
      updated_at,
      user_positions,
      user_abilities,
      user_restrictions,
      user_private,
      user_pay,
      user_emergency_contacts,
      user_roles,
      ...cleanFormData
    } = formData;

    // Handle avatar upload if present in metadata
    if (metadata?.avatarFile) {
      await userService.uploadAvatar(entityData.id, metadata.avatarFile);
    } else if (metadata?.avatarFile === null) {
      // User explicitly removed avatar
      await userService.deleteAvatar(entityData.id);
    }

    // Update the base users table (only direct columns)
    const updatedUser = await userService.updateUser(entityData.id, cleanFormData);

    // Update link tables if they were modified
    if (user_positions !== undefined) {
      await userService.syncUserPositions(entityData.id, user_positions || []);
    }

    if (user_abilities !== undefined) {
      await userService.syncUserAbilities(entityData.id, user_abilities || []);
    }

    if (user_restrictions !== undefined) {
      await userService.syncUserRestrictions(entityData.id, user_restrictions || []);
    }

    // Update user_private table if modified
    if (user_private !== undefined) {
      await userService.updateUserPrivate(entityData.id, user_private);
    }

    // Update user_pay table if modified
    if (user_pay !== undefined) {
      await userService.updateUserPay(entityData.id, user_pay);
    }

    // Sync emergency contacts if modified
    if (user_emergency_contacts !== undefined) {
      await userService.syncUserEmergencyContacts(entityData.id, user_emergency_contacts || []);
    }

    // Sync user roles if modified
    if (user_roles !== undefined) {
      const roleIds = (user_roles || []).map((r) => r.role.id);
      console.log("[UserEditProvider] Syncing roleIds:", roleIds);
      await userService.syncUserRoles(entityData.id, roleIds);
      console.log("[UserEditProvider] syncUserRoles completed");
    }

    // Fetch the complete user data with all joins to return
    const completeUser = await userService.getUserById(entityData.id);
    if (!completeUser) {
      throw new Error("Failed to fetch updated user data");
    }

    return completeUser;
  };

  return (
    <EntityEditProvider
      entityData={entityData}
      loading={loading}
      onSave={handleSave}
      onSaveSuccess={onSaveSuccess}
      entityName="User"
    >
      {children}
    </EntityEditProvider>
  );
}
